import {
  DispatchPricing,
  EquipmentType,
  PREMIUM_EQUIPMENT_TYPES,
} from '../constants/loadRequest';

// Dispatch pricing display (THT-691) — web port of tach-mobile's
// app/utils/dispatchPricing.ts. Resolves the rate for a selection: an
// all-standard selection shows only `standard_pct`, an all-premium (Box truck /
// Hotshot) selection shows only `box_hotshot_pct`, and a mixed selection shows
// both with their group labels. Pure display — no fee math, no linehaul
// estimates.
//
// As of the 2026-07-22 feedback this renders on the REVIEW step only: the input
// step and the confirmation carry no numbers at all.

export type PricingSelectionShape = 'standard' | 'premium' | 'mixed';

// Copy lives here, not in the modal, so the label/fallback are asserted by the
// pricing tests and can't drift between usages.
export const DISPATCH_PRICING_LABEL = 'Dispatch fee';
export const DISPATCH_PRICING_UNAVAILABLE =
  'Your dispatcher will confirm current rates with you.';

// Group labels for the mixed-tier string. "Standard equipment" per John's
// round-3 note — the original wording framed the tier as a leftover bucket,
// which these rates are not.
export const PREMIUM_TIER_LABEL = 'Box truck & Hotshot';
export const STANDARD_TIER_LABEL = 'Standard equipment';

export function getPricingSelectionShape(
  selected: EquipmentType[]
): PricingSelectionShape {
  const premiumCount = selected.filter((type) =>
    PREMIUM_EQUIPMENT_TYPES.has(type)
  ).length;
  if (premiumCount === 0) return 'standard';
  if (premiumCount === selected.length) return 'premium';
  return 'mixed';
}

// Runtime guard for the GET /emails/load-request/pricing body: a 2xx with a
// malformed payload degrades to the neutral fallback exactly like a failed
// call, instead of rendering "undefined%". Lives beside the display logic so
// every consumer of the endpoint validates the same way.
export function parseDispatchPricing(data: unknown): DispatchPricing | null {
  const candidate = data as Partial<DispatchPricing> | null | undefined;
  return typeof candidate?.standard_pct === 'number' &&
    typeof candidate?.box_hotshot_pct === 'number'
    ? (candidate as DispatchPricing)
    : null;
}

// At least one decimal place, matching John's display examples ("6.0%",
// "8.0%") — but never rounding away a finer backend rate (7.25 stays "7.25",
// not "7.3"): the displayed fee must be the fee that gets charged.
export const formatPct = (pct: number) => {
  const twoDecimals = pct.toFixed(2);
  return twoDecimals.endsWith('0') ? pct.toFixed(1) : twoDecimals;
};

export function formatDispatchPricing(
  pricing: DispatchPricing,
  selected: EquipmentType[]
): string {
  switch (getPricingSelectionShape(selected)) {
    case 'standard':
      return `${formatPct(pricing.standard_pct)}% of linehaul`;
    case 'premium':
      return `${formatPct(pricing.box_hotshot_pct)}% of linehaul`;
    case 'mixed':
    default:
      return `${PREMIUM_TIER_LABEL}: ${formatPct(pricing.box_hotshot_pct)}% · ${STANDARD_TIER_LABEL}: ${formatPct(pricing.standard_pct)}%`;
  }
}
